import { Component, computed, ElementRef, inject, ViewChild, effect, signal, OnInit } from '@angular/core';
import { ProgressService } from '../../lib/progress.service';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ProgressInfoComponent } from '../../progress-info/progress-info.component';
import {
  Address,
  COUNTRIES,
  Day,
  Product,
  ReservationRefererTypes,
  ReservationRefererTypesMapper,
  AddressInputComponent,
  getPlaceComponent,
} from '@shared';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { InputerrorsComponent } from '../../inputerrors.component';
import { ApiService } from '../../lib/api.service';
import { AuthService } from '../services/auth.service';
import { AddressListSelectorComponent } from '../address-list-selector/address-list-selector.component';

@Component({
  selector: 'app-booking-summary',
  imports: [
    CurrencyPipe,
    ProgressInfoComponent,
    DatePipe,
    ReactiveFormsModule,
    FormsModule,
    InputerrorsComponent,
    AddressInputComponent,
    AddressListSelectorComponent,
  ],
  templateUrl: './booking-summary.component.html',
  styleUrl: './booking-summary.component.css',
})
export class BookingSummaryComponent implements OnInit {
  public progressService = inject(ProgressService);
  public router = inject(Router);
  public apiService = inject(ApiService);
  public authService = inject(AuthService);
  public countries = COUNTRIES;

  @ViewChild('pickupTimeElem') pickupTimeElement: undefined|ElementRef;

  // Saved addresses (these would typically come from an API/service)
  public savedBillingAddresses = signal<Address[]>([]);
  public savedShippingAddresses = signal<Address[]>([]);

  // Selected addresses
  public selectedBillingAddress = signal<Address | undefined>(undefined);
  public selectedShippingAddress = signal<Address | undefined>(undefined);

  public refererOptions = computed<{ value: string; label: string }[]>(() =>
    Object.keys(ReservationRefererTypesMapper).map((objectKey: string) => {
      return {
        label:
          ReservationRefererTypesMapper[objectKey as ReservationRefererTypes],
        value: objectKey,
      };
    }),
  );

  public arrivalOptions = computed<Date[]>(() => {
    const startTime = new Date(
      `${this.selectedDaySlot()?.date} ${this.selectedDaySlot()?.time_from}`,
    );
    const endTime = new Date(
      `${this.selectedDaySlot()?.date} ${this.selectedDaySlot()?.time_latest}`,
    );
    const resultingTimes = [];

    for (
      let currentTime = startTime;
      currentTime <= endTime;
      currentTime.setMinutes(currentTime.getMinutes() + 30)
    ) {
      resultingTimes.push(new Date(currentTime));
    }

    return resultingTimes;
  });

  public grandTotal = computed<number>(
    () =>
      this.tokenSum() +
      this.basePriceSum() +
      (this.progressService.selectedShipping()?.price || 0),
  );

  public tokenSum = computed<number>(() =>
    this.bookedProducts()
      .map(
        (bookedProduct) =>
          +(bookedProduct.pivot!.amount || 0) *
          (+bookedProduct.price + +(bookedProduct.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0),
  );

  public bookedProducts = computed<Product[]>(
    () =>
      this.progressService
        .selectedAdditionalProducts()
        ?.filter((product) => +(product.pivot!.amount || 0) > 0) || [],
  );

  public basePriceSum = computed<number>(() => {
    if (!this.selectedDaySlot()) {
      return 0;
    }

    let pricePerPerson = 0;

    if (this.progressService.selectedPackage()) {
      pricePerPerson =
        +this.progressService.selectedPackage()!.price +
        (this.progressService
          .selectedPackage()!
          .products?.map((product) => +(product.bediengeld || 0))
          .reduce((prev, cur) => prev + cur, 0) || 0);
    } else {
      pricePerPerson = this.getCostForProducts(
        this.selectedDaySlot()?.products || [],
      );
    }

    return pricePerPerson * (this.progressService.selectedPersons() || 0);
  });

  public selectedDaySlot = computed<Day | null>(() => {
    if (!this.progressService.selectedLodge()) {
      return null;
    }

    return (
      this.progressService
        .selectedLodge()
        ?.days?.find(
          (daySlot) =>
            daySlot.daytype === this.progressService.selectedDayslot() &&
            daySlot.date === this.progressService.selectedDate(),
        ) || null
    );
  });

  // Personal information form (new structure according to Figma)
  public personalInfoForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    surname: new FormControl('', [Validators.required]),
    id_number: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    city: new FormControl('', [Validators.required]),
    street: new FormControl('', [Validators.required]),
    street_nr: new FormControl('', [Validators.required]),
    zipcode: new FormControl('', [Validators.required]),
  });

  // Old invoice form (kept for compatibility)
  // Note: customer_type is set to 'person' by default since this field is not shown in the UI
  public invoiceForm = new FormGroup({
    customer_type: new FormControl('person'), // Default value 'person' (same as registration)
    customer_nr_old: new FormControl(''),
    typename: new FormControl(''),
  });

  // Shipping address form (for different address option)
  public shippingAddressForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    surname: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    street: new FormControl('', [Validators.required]),
    street_nr: new FormControl('', [Validators.required]),
    zipcode: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
  });

  public useDifferentShippingAddress = false;
  public deliveryAdressEqualsInvoiceAdress = true;
  public additionalComment = "";
  public referer = "";
  public acceptTermsAndConditions = false;
  public triedToSubmit = false;
  public pickupTime = '';

  @ViewChild('cityInput') cityInput?: ElementRef;
  @ViewChild('streetInput') streetInput?: ElementRef;

  ngOnInit(): void {
    // Load addresses only if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.loadSavedAddresses();

      // For authenticated users, pre-fill and disable email field
      const userEmail = this.authService.getUserEmail();
      if (userEmail) {
        this.personalInfoForm.controls.email.setValue(userEmail);
        this.personalInfoForm.controls.email.disable(); // Disable to prevent editing
      } else {
        // Try to fetch email from backend if not in session storage
        this.authService.checkUserLoggedIn().subscribe({
          next: (response) => {
            if (response.logged_in && response.user?.email) {
              // Store email in session storage for future use
              sessionStorage.setItem('userEmail', response.user.email);
              this.personalInfoForm.controls.email.setValue(response.user.email);
              this.personalInfoForm.controls.email.disable();
            } else {
              // If still no email available, keep it enabled but optional
              this.personalInfoForm.controls.email.clearValidators();
              this.personalInfoForm.controls.email.updateValueAndValidity();
            }
          },
          error: () => {
            // On error, keep email field enabled but optional
            this.personalInfoForm.controls.email.clearValidators();
            this.personalInfoForm.controls.email.updateValueAndValidity();
          }
        });
      }
    }
  }

  /**
   * Load saved addresses from API
   */
  private loadSavedAddresses(): void {
    this.authService.getCustomerAddresses().subscribe({
      next: (addresses) => {
        this.savedBillingAddresses.set(
          addresses.filter(a => a.address_type === 'invoice' || a.address_type === 'default')
        );
        this.savedShippingAddresses.set(
          addresses.filter(a => a.address_type === 'delivery')
        );

        // Auto-select default address (address_type === 'default')
        const defaultBilling = this.savedBillingAddresses().find(a => a.address_type === 'default');
        if (defaultBilling) {
          this.selectedBillingAddress.set(defaultBilling);
          this.prefillPersonalInfoForm(defaultBilling);
        } else if (this.savedBillingAddresses().length > 0) {
          // Falls keine Standard-Adresse, wähle die erste
          this.selectedBillingAddress.set(this.savedBillingAddresses()[0]);
          this.prefillPersonalInfoForm(this.savedBillingAddresses()[0]);
        }

        const defaultShipping = this.savedShippingAddresses().find(a => a.address_type === 'default');
        if (defaultShipping) {
          this.selectedShippingAddress.set(defaultShipping);
        } else if (this.savedShippingAddresses().length > 0) {
          // Falls keine Standard-Adresse, wähle die erste
          this.selectedShippingAddress.set(this.savedShippingAddresses()[0]);
        }
      },
      error: (error) => {
        console.error('Error loading addresses:', error);
        // Keep empty arrays on error
        this.savedBillingAddresses.set([]);
        this.savedShippingAddresses.set([]);
      }
    });
  }

  /**
   * Handle billing address selection
   */
  onBillingAddressSelected(address: Address): void {
    this.selectedBillingAddress.set(address);
    // Also prefill the personal info form with this address
    this.prefillPersonalInfoForm(address);
  }

  /**
   * Handle new billing address save
   */
  onBillingAddressAdded(address: Address): void {
    if (this.authService.isAuthenticated()) {
      this.authService.saveAddress(address).subscribe({
        next: (savedAddress) => {
          this.savedBillingAddresses.update(addresses => [...addresses, savedAddress]);
          this.selectedBillingAddress.set(savedAddress);
          // Also prefill the personal info form with this new address
          this.prefillPersonalInfoForm(savedAddress);
        },
        error: (error) => {
          console.error('Error saving address:', error);
        }
      });
    } else {
      // For guest users, just add to local array
      const newAddress = { ...address, id: Date.now() };
      this.savedBillingAddresses.update(addresses => [...addresses, newAddress]);
      this.selectedBillingAddress.set(newAddress);
      // Also prefill the personal info form with this new address
      this.prefillPersonalInfoForm(newAddress);
    }
  }

  /**
   * Handle "use same address" for billing
   */
  onUseSameAddressForBilling(): void {
    // Copy data from personalInfoForm to billing address
    const addressFromPersonalInfo: Address = {
      address_type: 'invoice',
      salutation: 'none', // Default salutation (required by backend)
      title: undefined, // No academic title by default (required by backend)
      typename: undefined, // No company/association name (required by backend)
      name: this.personalInfoForm.value.name!,
      surname: this.personalInfoForm.value.surname!,
      street: this.personalInfoForm.value.street!,
      street_nr: this.personalInfoForm.value.street_nr!,
      street_extra: undefined, // No extra address line (required by backend)
      city: this.personalInfoForm.value.city!,
      zipcode: this.personalInfoForm.value.zipcode!,
      country: 'DE',
    };
    this.selectedBillingAddress.set(addressFromPersonalInfo);
  }

  /**
   * Handle shipping address selection
   */
  onShippingAddressSelected(address: Address): void {
    this.selectedShippingAddress.set(address);
  }

  /**
   * Handle new shipping address save
   */
  onShippingAddressAdded(address: Address): void {
    if (this.authService.isAuthenticated()) {
      this.authService.saveAddress(address).subscribe({
        next: (savedAddress) => {
          this.savedShippingAddresses.update(addresses => [...addresses, savedAddress]);
          this.selectedShippingAddress.set(savedAddress);
        },
        error: (error) => {
          console.error('Error saving address:', error);
        }
      });
    } else {
      // For guest users, just add to local array
      const newAddress = { ...address, id: Date.now() };
      this.savedShippingAddresses.update(addresses => [...addresses, newAddress]);
      this.selectedShippingAddress.set(newAddress);
    }
  }

  /**
   * Handle "use same address" for shipping
   */
  onUseSameAddressForShipping(): void {
    // Use the selected billing address for shipping
    if (this.selectedBillingAddress()) {
      const shippingAddress: Address = {
        ...this.selectedBillingAddress()!,
        address_type: 'delivery',
        salutation: this.selectedBillingAddress()!.salutation || 'none', // Ensure salutation is present
        title: this.selectedBillingAddress()!.title || undefined, // Ensure title is present
        typename: this.selectedBillingAddress()!.typename || undefined, // Ensure typename is present
        street_extra: this.selectedBillingAddress()!.street_extra || undefined, // Ensure street_extra is present
        id: undefined, // New address
      };
      this.selectedShippingAddress.set(shippingAddress);
    }
  }


  getCostForProducts(products: Product[]) {
    return products
      ?.map(
        (product) =>
          +(product.pivot?.amount || 1) *
          (+product.price + +(product.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0);
  }

  confirmBooking() {
    this.personalInfoForm.markAllAsTouched();
    this.triedToSubmit = true;

    if(!this.pickupTime) {
      alert('Bitte wählen Sie eine Abholzeit aus.');
      this.pickupTimeElement?.nativeElement.scrollIntoView({ behavior: "smooth", block: "start"})
      return;
    }

    if(!this.acceptTermsAndConditions) {
      alert('Bitte akzeptieren Sie die AGB und Datenschutzerklärung, um fortzufahren.');
      const termsCheckbox = document.querySelector('input[type="checkbox"]');
      if (termsCheckbox) {
        termsCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Validate personal info form
    if (!this.personalInfoForm.valid) {
      const errors = this.getFormValidationErrors(this.personalInfoForm);
      console.error('❌ Personal Info Form Invalid:', this.personalInfoForm.value);
      console.error('Personal Info Form Errors:', errors);

      // Create user-friendly error message
      const fieldNames: {[key: string]: string} = {
        'name': 'Vorname',
        'surname': 'Nachname',
        'id_number': 'Ausweisnummer',
        'phone': 'Telefon',
        'email': 'E-Mail',
        'city': 'Stadt',
        'street': 'Straße',
        'street_nr': 'Hausnummer',
        'zipcode': 'PLZ'
      };

      const missingFields = errors.map(err => {
        const match = err.match(/Feld "(.+?)":/);
        if (match && match[1]) {
          return fieldNames[match[1]] || match[1];
        }
        return err;
      });

      alert('Bitte füllen Sie folgende Pflichtfelder aus:\n• ' + missingFields.join('\n• '));
      const firstErrorElem = document.querySelector('form .ng-invalid');
      if (firstErrorElem) {
        firstErrorElem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    // Note: invoiceForm validation removed as it's no longer used in the UI
    // customer_type is set to 'person' by default

    // Validate personal info form
    if (!this.personalInfoForm.valid) {
      console.error('❌ Personal Info Form Invalid:', this.personalInfoForm.value);
      console.error('Personal Info Form Errors:', this.getFormValidationErrors(this.personalInfoForm));
      alert('Bitte füllen Sie alle Pflichtfelder bei den persönlichen Informationen aus:\n' +
            this.getFormValidationErrors(this.personalInfoForm).join('\n'));
      const firstErrorElem = document.querySelector('form .ng-invalid');
      if (firstErrorElem) {
        firstErrorElem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    // Check grandTotal
    if (this.grandTotal() < 1) {
      alert(
        'Da ist leider etwas sehr schiefgelaufen. Bitte laden Sie die Seite erneut',
      );
      return;
    }

    // For authenticated users: Validate shipping address selection if required
    if (this.authService.isAuthenticated() &&
        this.progressService.selectedShipping()?.requiresCountryCodeInDeliveryAdress !== false) {
      if (!this.selectedShippingAddress()) {
        alert('Bitte wählen Sie eine Lieferadresse aus oder geben Sie an, dass die Rechnungsadresse verwendet werden soll.');
        return;
      }
    }

    // For guests: Validate shipping address form if different address is selected
    if (!this.authService.isAuthenticated() &&
        this.useDifferentShippingAddress &&
        !this.shippingAddressForm.valid) {
      alert('Bitte füllen Sie alle Pflichtfelder bei der Lieferadresse aus.');
      this.shippingAddressForm.markAllAsTouched();
      const firstErrorElem = document.querySelector('form .ng-invalid');
      if (firstErrorElem) {
        firstErrorElem.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    // All validations passed - proceed with booking
    const addresses: {
      defaultAddress?: Address;
      invoiceAddress?: Address;
    } = {};

    // Create billing address from personal info form
    const personalInfo = this.personalInfoForm.getRawValue();
    const billingAddressFromPersonalInfo: Address = {
      address_type: 'invoice',
      salutation: 'none', // Default salutation (required by backend email template)
      title: undefined, // No academic title by default (required by backend email template)
      typename: undefined, // No company/association name (required by backend email template)
      name: personalInfo.name || undefined,
      surname: personalInfo.surname || undefined,
      street: personalInfo.street || undefined,
      street_nr: personalInfo.street_nr || undefined,
      street_extra: undefined, // No extra address line (required by backend email template)
      city: personalInfo.city || undefined,
      zipcode: personalInfo.zipcode || undefined,
      country: 'DE',
    };

    // For authenticated users: Use selected shipping address
    if (this.authService.isAuthenticated() && this.selectedShippingAddress()) {
      addresses.defaultAddress = this.selectedShippingAddress()!;
      addresses.invoiceAddress = billingAddressFromPersonalInfo;

      // Track address usage for statistics
      if (this.selectedShippingAddress()!.id) {
        this.authService.trackAddressUsage(this.selectedShippingAddress()!.id!).subscribe();
      }
    }
    // For guests: Use shipping form or same address
    else if (this.useDifferentShippingAddress && this.shippingAddressForm.valid) {
      // Use different shipping address
      addresses.defaultAddress = {
        address_type: 'delivery',
        salutation: 'none', // Default salutation (required by backend)
        title: undefined, // No academic title by default (required by backend)
        typename: undefined, // No company/association name (required by backend)
        name: this.shippingAddressForm.value.name!,
        surname: this.shippingAddressForm.value.surname!,
        street: this.shippingAddressForm.value.street!,
        street_nr: this.shippingAddressForm.value.street_nr!,
        street_extra: undefined, // No extra address line (required by backend)
        city: this.shippingAddressForm.value.city!,
        zipcode: this.shippingAddressForm.value.zipcode!,
        country: 'DE',
      };
      addresses.invoiceAddress = billingAddressFromPersonalInfo;
    } else {
      // Use same address for billing and shipping
      addresses.defaultAddress = {
        ...billingAddressFromPersonalInfo,
        address_type: 'default',
      };
    }

    this.apiService
      .submitBookingRequest({
        ...addresses,
        selectedFestival: {
          festivalId: this.progressService.selectedFestival()!.id || 0,
          festivalTitle:
            this.progressService.selectedFestival()!.title_public ||
            this.progressService.selectedFestival()!.title,
        },
        selectedLodge: {
          lodgeId: this.progressService.selectedLodge()!.id || 0,
          lodgeTitle:
            this.progressService.selectedLodge()!.title_public ||
            this.progressService.selectedLodge()!.title,
          lodgeTableFactor: [],
          lodgeProducts: this.progressService
            .selectedAdditionalProducts()
            .map((product) => {
              product.amount = product.pivot?.amount;
              return product;
            }),
        },
        selectedDate: this.progressService.selectedDate()!,
        selectedDaytime: this.progressService.selectedDayslot()!,
        selectedPersons: this.progressService.selectedPersons()!,
        selectedDay: {
          type: this.progressService.selectedPackage()
            ? 'package'
            : 'product',
          // @ts-ignore
          day: this.selectedDaySlot()!,
        },
        selectedPickupTime: this.pickupTime,
        selectedDelivery: this.progressService.selectedShipping()!,
        customerData: {
          customer_nr_old:
            this.invoiceForm.controls.customer_nr_old.value || null,
          // @ts-ignore
          customer_type: this.invoiceForm.controls.customer_type.value,
          // Use getRawValue() to get value even if field is disabled
          email: this.personalInfoForm.getRawValue().email || undefined,
          phone: this.personalInfoForm.controls.phone.value || undefined,
          mobile: undefined, // Mobile is no longer in the form
        },
        selectedDefaultPackage:
          this.progressService.selectedPackage() || undefined,
        selectedDefaultProducts: this.selectedDaySlot()!.products,
        referer: this.referer,
        notes: this.additionalComment
      })
      .subscribe({
        next: (data) => {
          this.router.navigate(['/success']);
        },
        error: (e) => {
          alert(
            'Bei der Übermittlung der Reservierung ist ein Fehler aufgetreten. Bitte erneut versuchen',
          );
          console.error(e);
        },
      });
  }

  /**
   * Handle address selection from Google Places autocomplete for personal info
   */
  onPersonalAddressSelected(place: google.maps.places.PlaceResult): void {
    const street = getPlaceComponent(place, 'route');
    const streetNumber = getPlaceComponent(place, 'street_number');
    const zipcode = getPlaceComponent(place, 'postal_code');
    const city = getPlaceComponent(place, 'locality');

    if (street) {
      this.personalInfoForm.controls.street.setValue(street);
      this.personalInfoForm.controls.street.markAsTouched();
    }

    if (streetNumber) {
      this.personalInfoForm.controls.street_nr.setValue(streetNumber);
      this.personalInfoForm.controls.street_nr.markAsTouched();
    }

    if (zipcode) {
      this.personalInfoForm.controls.zipcode.setValue(zipcode);
      this.personalInfoForm.controls.zipcode.markAsTouched();
    }

    if (city) {
      this.personalInfoForm.controls.city.setValue(city);
      this.personalInfoForm.controls.city.markAsTouched();
    }
  }

  /**
   * Prefill personal info form with saved address data
   */
  private prefillPersonalInfoForm(address: Address): void {
    if (!address) return;

    // Prefill name fields
    if (address.name) {
      this.personalInfoForm.controls.name.setValue(address.name);
      this.personalInfoForm.controls.name.markAsTouched();
    }

    if (address.surname) {
      this.personalInfoForm.controls.surname.setValue(address.surname);
      this.personalInfoForm.controls.surname.markAsTouched();
    }

    // Prefill address fields
    if (address.street) {
      this.personalInfoForm.controls.street.setValue(address.street);
      this.personalInfoForm.controls.street.markAsTouched();
    }

    if (address.street_nr) {
      this.personalInfoForm.controls.street_nr.setValue(address.street_nr);
      this.personalInfoForm.controls.street_nr.markAsTouched();
    }

    if (address.zipcode) {
      this.personalInfoForm.controls.zipcode.setValue(address.zipcode);
      this.personalInfoForm.controls.zipcode.markAsTouched();
    }

    if (address.city) {
      this.personalInfoForm.controls.city.setValue(address.city);
      this.personalInfoForm.controls.city.markAsTouched();
    }
  }

  /**
   * Handle address selection from Google Places autocomplete for shipping address form
   */
  onShippingAddressAutocomplete(place: google.maps.places.PlaceResult): void {
    const street = getPlaceComponent(place, 'route');
    const streetNumber = getPlaceComponent(place, 'street_number');
    const zipcode = getPlaceComponent(place, 'postal_code');
    const city = getPlaceComponent(place, 'locality');

    if (street) {
      this.shippingAddressForm.controls.street.setValue(street);
      this.shippingAddressForm.controls.street.markAsTouched();
    }

    if (streetNumber) {
      this.shippingAddressForm.controls.street_nr.setValue(streetNumber);
      this.shippingAddressForm.controls.street_nr.markAsTouched();
    }

    if (zipcode) {
      this.shippingAddressForm.controls.zipcode.setValue(zipcode);
      this.shippingAddressForm.controls.zipcode.markAsTouched();
    }

    if (city) {
      this.shippingAddressForm.controls.city.setValue(city);
      this.shippingAddressForm.controls.city.markAsTouched();
    }
  }

  /**
   * Helper method to get detailed form validation errors for debugging
   */
  private getFormValidationErrors(form: FormGroup): string[] {
    const errors: string[] = [];
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control && !control.valid) {
        const controlErrors = control.errors;
        if (controlErrors) {
          Object.keys(controlErrors).forEach(errorKey => {
            errors.push(`- Feld "${key}": ${errorKey}`);
          });
        }
      }
    });
    return errors.length > 0 ? errors : ['Unbekannter Fehler'];
  }
}
